import { useEffect, useRef, useState } from "react";
import type { PresetDocument } from "@lpa/preset-model";

const imageCache = new Map<string, Promise<ImageData>>();

function loadImageData(src: string): Promise<ImageData> {
  const cached = imageCache.get(src); if (cached) return cached;
  const result = new Promise<ImageData>((resolve, reject) => {
    const image = new Image(); image.decoding = "async";
    image.onload = () => {
      const scale = Math.min(1, 1200 / image.naturalWidth); const width = Math.round(image.naturalWidth * scale); const height = Math.round(image.naturalHeight * scale);
      const canvas = document.createElement("canvas"); canvas.width = width; canvas.height = height;
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) { reject(new Error("Canvas 2D is niet beschikbaar.")); return; }
      context.drawImage(image, 0, 0, width, height); resolve(context.getImageData(0, 0, width, height));
    };
    image.onerror = () => reject(new Error(`Referentiefoto kon niet worden geladen: ${src}`)); image.src = src;
  });
  imageCache.set(src, result); return result;
}

export function PresetCanvas({ src, preset, className, ariaLabel }: { src: string; preset: PresetDocument; className?: string; ariaLabel: string }) {
  const ref = useRef<HTMLCanvasElement>(null); const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  useEffect(() => {
    let cancelled = false; let worker: Worker | undefined; setState("loading");
    void loadImageData(src).then((original) => {
        if (cancelled) return;
        worker = new Worker(new URL("./render.worker.ts", import.meta.url), { type: "module" });
        worker.onmessage = (event: MessageEvent<{ data: ArrayBuffer; width: number; height: number; renderer: "webgl2" | "cpu" | "cpu-parity-fallback"; parityMaxDelta?: number; paritySample?: string; webglError?: string }>) => {
        if (cancelled || !ref.current) return;
        ref.current.width = event.data.width; ref.current.height = event.data.height;
        const context = ref.current.getContext("2d");
        if (!context) throw new Error("Canvas 2D is niet beschikbaar.");
        context.putImageData(new ImageData(new Uint8ClampedArray(event.data.data), event.data.width, event.data.height), 0, 0); ref.current.dataset.renderer = event.data.renderer; if (event.data.parityMaxDelta !== undefined) ref.current.dataset.parityMaxDelta = String(event.data.parityMaxDelta); if (event.data.paritySample) ref.current.dataset.paritySample = event.data.paritySample; if (event.data.webglError) ref.current.dataset.webglError = event.data.webglError; setState("ready"); worker?.terminate();
        };
        worker.onerror = () => { if (!cancelled) setState("error"); };
        const input = new Uint8ClampedArray(original.data.length); input.set(original.data);
        worker.postMessage({ data: input.buffer, width: original.width, height: original.height, settings: preset.settings }, [input.buffer]);
      }).catch(() => { if (!cancelled) setState("error"); });
    return () => { cancelled = true; worker?.terminate(); };
  }, [src, preset]);
  return <span className={`preset-canvas-wrap ${className ?? ""}`} data-state={state}><canvas ref={ref} role="img" aria-label={ariaLabel}/>{state === "loading" && <span className="canvas-loading" aria-live="polite">Renderen…</span>}{state === "error" && <span className="canvas-error">Preview kon niet worden gerenderd.</span>}</span>;
}
